import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import { isAdminPortalUser } from "../../helpers/adminRoles"
import {
  getAuthenticatedHomePath,
  hasAuthSession,
} from "../../helpers/routeAccess"
import {
  fetchActiveSession,
  getActiveSessionSnapshot,
  markUnloadHandled,
  pauseActiveSession,
  resolveShouldPause,
  syncActiveSessionSnapshot,
} from "../../helpers/activeSession"
import {
  performAppLogout,
  withSessionCheckTimeout,
} from "../../helpers/sessionLogout"
import { TIMELOG_REASONS } from "../../helpers/sessionDetail"
import { ActiveSessionLogoutModal } from "../../components/Modals/SessionModals"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const Logout = () => {
  document.title = "Logout | AMOK . EARTH"
  const navigate = useNavigate()
  const currentUser = useSelector(state => state.auth.currentUser)
  const token = useSelector(state => state.auth.jid)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const startedRef = useRef(false)

  const homePath = getAuthenticatedHomePath(currentUser)

  const finishLogout = useCallback(async () => {
    markUnloadHandled()
    // API first (while token valid), then clear session, then login + toast.
    await performAppLogout({ notifyServer: true })
    navigate("/login", { replace: true })
    toast.success("Logout Successfully", TOAST_OPTIONS)
  }, [navigate])

  const pauseThenLogout = useCallback(async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    markUnloadHandled()
    const active = getActiveSessionSnapshot()
    await performAppLogout({
      notifyServer: true,
      pauseActive: active.sessionId
        ? () =>
            pauseActiveSession({
              sessionId: active.sessionId,
              shouldPause: active.shouldPause,
              reason: TIMELOG_REASONS.logout,
            })
        : null,
    })
    navigate("/login", { replace: true })
    toast.success("Logout Successfully", TOAST_OPTIONS)
  }, [isSubmitting, navigate])

  const staySignedIn = useCallback(() => {
    if (isSubmitting) return
    setConfirmOpen(false)
    navigate(homePath, { replace: true })
  }, [homePath, isSubmitting, navigate])

  useEffect(() => {
    if (startedRef.current) return undefined
    startedRef.current = true

    const startLogout = async () => {
      if (!hasAuthSession(token)) {
        navigate("/login", { replace: true })
        return
      }

      // Admin / city-admin never run live member sessions.
      if (isAdminPortalUser(currentUser)) {
        await finishLogout()
        return
      }

      const snap = getActiveSessionSnapshot()
      if (snap.sessionId && snap.shouldPause) {
        setConfirmOpen(true)
        return
      }

      // Cap session lookup so slow networks cannot hang on /logout forever.
      const result = await withSessionCheckTimeout(fetchActiveSession())
      if (result?.session?.id) {
        const shouldPause = resolveShouldPause(
          result.session,
          currentUser?.id,
        )
        syncActiveSessionSnapshot({
          sessionId: String(result.session.id),
          token,
          shouldPause,
        })
        if (shouldPause) {
          setConfirmOpen(true)
          return
        }
      }

      await finishLogout()
    }

    startLogout()
    return undefined
    // Run once on mount for this logout visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!confirmOpen) return undefined
    document.body.classList.add("session-guard-modal-open")
    return () => document.body.classList.remove("session-guard-modal-open")
  }, [confirmOpen])

  return (
    <ActiveSessionLogoutModal
      isOpen={confirmOpen}
      onCancel={staySignedIn}
      onConfirm={pauseThenLogout}
      isSubmitting={isSubmitting}
    />
  )
}

export default Logout
